import type { AiFacilitatorResult } from "../../types";
import type { RuntimeCustomerValueCluster, RuntimeWorkspaceOverview } from "../api/runtimeWorkspaceClient";
import { getHauffeCustomerValueMethodNote } from "../customerValue/hauffeCustomerValueMethodology";
import { containsForbiddenGermanCustomerValueTerms, hasUsableCustomerValueContent } from "./contentRepair";
import type { WorkspaceLanguage } from "./index";

function emptyForMissingLanguage(language: WorkspaceLanguage): string {
  return language === "en" ? "Content is not available in English yet." : "Inhalt ist in dieser Sprache noch nicht verfügbar.";
}

function isNonEmptyText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isValidForLanguage(value: unknown, language: WorkspaceLanguage): boolean {
  return language !== "en" || !containsForbiddenGermanCustomerValueTerms(value);
}

type ClusterContentI18n = NonNullable<RuntimeCustomerValueCluster["contentI18n"]>;
type ClusterTranslation = NonNullable<ClusterContentI18n[WorkspaceLanguage]>;

type Calculation = NonNullable<RuntimeWorkspaceOverview["calculation"]>;
type Criterion = NonNullable<Calculation["criteria"]>[number];
type CriterionTranslation = NonNullable<NonNullable<Criterion["contentI18n"]>[WorkspaceLanguage]>;
type Segment = NonNullable<Calculation["segments"]>[number];
type ScoreTemplate = NonNullable<Calculation["scoreTemplates"]>[number];
type ScoreTemplateTranslation = NonNullable<NonNullable<ScoreTemplate["contentI18n"]>[WorkspaceLanguage]>;

function hasUsableClusterTranslation(
  translated: ClusterTranslation | undefined,
  language: WorkspaceLanguage
): translated is ClusterTranslation {
  return Boolean(
    translated &&
    isNonEmptyText(translated.name) &&
    isNonEmptyText(translated.description) &&
    isValidForLanguage(translated, language)
  );
}

function hasUsableCriterionTranslation(
  translated: CriterionTranslation | undefined,
  language: WorkspaceLanguage
): translated is CriterionTranslation {
  return Boolean(
    translated &&
    isNonEmptyText(translated.name) &&
    isNonEmptyText(translated.description) &&
    isValidForLanguage(translated, language)
  );
}

function hasUsableSegment(segment: Segment | undefined, language: WorkspaceLanguage): segment is Segment {
  return Boolean(
    segment &&
    hasUsableCustomerValueContent(segment) &&
    isValidForLanguage(segment, language)
  );
}

function hasUsableSegments(segments: Segment[] | undefined, language: WorkspaceLanguage): segments is Segment[] {
  return Boolean(segments?.length && segments.every((segment) => hasUsableSegment(segment, language)));
}

function hasUsableScoreTemplateTranslation(
  translated: ScoreTemplateTranslation | undefined,
  language: WorkspaceLanguage
): translated is ScoreTemplateTranslation {
  return Boolean(
    translated &&
    isNonEmptyText(translated.segmentName) &&
    isNonEmptyText(translated.recommendation) &&
    isValidForLanguage(translated, language)
  );
}

export function resolveAiSummaryForLanguage(
  summary: RuntimeWorkspaceOverview["latestSummary"] | undefined,
  language: WorkspaceLanguage
): AiFacilitatorResult | undefined {
  return summary?.resultI18n?.[language];
}

export function resolveRuntimeClusterForLanguage(
  cluster: RuntimeCustomerValueCluster,
  language: WorkspaceLanguage
): RuntimeCustomerValueCluster {
  const translated = cluster.contentI18n?.[language];
  if (hasUsableClusterTranslation(translated, language)) {
    return {
      ...cluster,
      name: translated.name,
      description: translated.description,
      sourceTerms: translated.sourceTerms,
      sourceDepartments: translated.sourceDepartments,
      unit: translated.unit
    };
  }
  return {
    ...cluster,
    name: emptyForMissingLanguage(language),
    description: emptyForMissingLanguage(language),
    sourceTerms: [],
    sourceDepartments: [],
    unit: undefined
  };
}

export function resolveCalculationForLanguage(
  calculation: RuntimeWorkspaceOverview["calculation"],
  language: WorkspaceLanguage
): RuntimeWorkspaceOverview["calculation"] {
  if (!calculation) return calculation;
  const resolvedSegments = calculation.segmentsI18n?.[language];
  return {
    ...calculation,
    methodNote: getHauffeCustomerValueMethodNote(language),
    segments: hasUsableSegments(resolvedSegments, language) ? resolvedSegments : [],
    criteria: calculation.criteria?.map((criterion) => {
      const translated = criterion.contentI18n?.[language];
      if (hasUsableCriterionTranslation(translated, language)) {
        return { ...criterion, name: translated.name, description: translated.description };
      }
      return { ...criterion, name: emptyForMissingLanguage(language), description: emptyForMissingLanguage(language) };
    }),
    scoreTemplates: calculation.scoreTemplates?.map((template) => {
      const translated = template.contentI18n?.[language];
      if (hasUsableScoreTemplateTranslation(translated, language)) {
        return { ...template, segmentName: translated.segmentName, recommendation: translated.recommendation };
      }
      return { ...template, segmentName: emptyForMissingLanguage(language), recommendation: emptyForMissingLanguage(language) };
    })
  };
}

export function hasResolvedClusterLanguage(clusters: RuntimeCustomerValueCluster[], language: WorkspaceLanguage): boolean {
  return clusters.length > 0 && clusters.every((cluster) => hasUsableClusterTranslation(cluster.contentI18n?.[language], language));
}

export function hasResolvedCalculationLanguage(
  calculation: RuntimeWorkspaceOverview["calculation"] | undefined,
  language: WorkspaceLanguage
): boolean {
  if (!calculation) return true;
  const hasCriteria = (calculation.criteria ?? []).every((criterion) =>
    hasUsableCriterionTranslation(criterion.contentI18n?.[language], language)
  );
  const hasSegments = hasUsableSegments(calculation.segmentsI18n?.[language], language);
  const hasScoreTemplates = (calculation.scoreTemplates ?? []).every((template) =>
    hasUsableScoreTemplateTranslation(template.contentI18n?.[language], language)
  );
  return hasCriteria && hasSegments && hasScoreTemplates;
}
